// First-party ad telemetry → mogul custom events (ad_impression / ad_click).
//
// Feeds the public sponsor dashboards (mogul /share/<sponsor>). Every event
// carries {sponsor, placement, unit}; mogul adds page/geo/device context on
// its own. The ad_* event names deliberately skip session tracking on the
// mogul side (they'd distort bounce/duration), so fire-and-forget freely —
// and never rename them to something session-meaningful.
//
// Impressions use viewability semantics: fired at most once per mount, only
// after the unit has been ≥50% visible for 1 continuous second. "The HTML
// rendered" is not an impression. Attach the returned ref to the ad's
// container element; if the component renders nothing (ad-free member),
// the ref never attaches and nothing fires.
import { useEffect, useRef } from 'react'
import track from '@/utils/mogul'

export const trackAdClick = (sponsor, placement, unit) => {
  track('ad_click', { sponsor, placement, unit })
}

export const useAdImpression = (sponsor, placement, unit) => {
  const ref = useRef(null)
  const fired = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (fired.current || !el || typeof IntersectionObserver === 'undefined') { return undefined }

    let timer = null
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio >= 0.5) {
          if (!timer && !fired.current) {
            timer = setTimeout(() => {
              fired.current = true
              track('ad_impression', { sponsor, placement, unit })
              observer.disconnect()
            }, 1000)
          }
        } else if (timer) {
          clearTimeout(timer)
          timer = null
        }
      })
    }, { threshold: [0, 0.5] })

    observer.observe(el)
    return () => {
      if (timer) { clearTimeout(timer) }
      observer.disconnect()
    }
  }, [sponsor, placement, unit])

  return ref
}
